import { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import {
  geocodeAddress, haversineDistanceMiles,
  CATEGORY_CONFIG, buildListingUrl,
} from '@/lib/index';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, Search, Copy, Check, ExternalLink, Code2,
  Loader, Trash2, Clock, ChevronRight, Share2,
  ArrowLeft, Sparkles, Info,
} from 'lucide-react';

// ─── Brand ────────────────────────────────────────────────────────────────
const BRAND = {
  red: '#CC0000',
  navy: '#003087',
  gold: '#B8860B',
  darkNavy: '#002060',
};

// ─── Types ────────────────────────────────────────────────────────────────
interface SavedListing {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  createdAt: string;
}

const STORAGE_KEY = 'remax_saved_listings';

function loadSaved(): SavedListing[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveListing(listing: SavedListing) {
  const all = loadSaved().filter((l) => l.id !== listing.id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([listing, ...all].slice(0, 20)));
}

function deleteListing(id: string) {
  const all = loadSaved().filter((l) => l.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

// ─── Mini Map Preview ─────────────────────────────────────────────────────
function MiniMap({
  lat, lng, name, onDragEnd,
}: { lat: number; lng: number; name: string; onDragEnd: (lat: number, lng: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { center: [lat, lng], zoom: 14, zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    const goldIcon = L.divIcon({
      html: `<div style="width:36px;height:36px;background:${BRAND.gold};border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4)"></div>`,
      className: '',
      iconSize: [36, 36],
      iconAnchor: [18, 36],
    });

    const marker = L.marker([lat, lng], { icon: goldIcon, draggable: true })
      .addTo(map)
      .bindTooltip(name, { permanent: true, direction: 'top', offset: [0, -40], className: 'listing-tooltip' });

    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      onDragEnd(pos.lat, pos.lng);
    });

    mapRef.current = map;
    markerRef.current = marker;

    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update marker + center when lat/lng change
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    markerRef.current.setLatLng([lat, lng]);
    mapRef.current.flyTo([lat, lng], 14, { duration: 1.0 });
    markerRef.current.setTooltipContent(name);
  }, [lat, lng, name]);

  return (
    <>
      <style>{`.listing-tooltip { background:${BRAND.darkNavy};color:white;border:none;font-weight:700;font-size:11px;padding:4px 8px;border-radius:6px; }`}</style>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </>
  );
}

// ─── Copy Button ──────────────────────────────────────────────────────────
function CopyBtn({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg text-white transition-all"
      style={{ background: copied ? '#2E7D32' : BRAND.darkNavy }}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function CreateListing() {
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [tagline, setTagline] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [geoError, setGeoError] = useState('');

  // Result state
  const [pinned, setPinned] = useState<{ lat: number; lng: number } | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [embedCode, setEmbedCode] = useState('');

  // Saved listings
  const [saved, setSaved] = useState<SavedListing[]>(() => loadSaved());

  // Suggestions from Nominatim
  const [suggestions, setSuggestions] = useState<{ label: string; lat: number; lng: number }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live address suggestions
  useEffect(() => {
    if (address.length < 5) { setSuggestions([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSuggestionLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=5&countrycodes=us&addressdetails=1`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en', 'User-Agent': 'SoldOnDanielle/1.0' } });
        const data = await res.json();
        setSuggestions(data.map((d: { display_name: string; lat: string; lon: string }) => ({
          label: d.display_name,
          lat: parseFloat(d.lat),
          lng: parseFloat(d.lon),
        })));
        setShowSuggestions(true);
      } catch { /* ignore */ } finally {
        setSuggestionLoading(false);
      }
    }, 450);
  }, [address]);

  const buildOutputs = useCallback((n: string, a: string, lat: number, lng: number, tl: string) => {
    const fakeAmenity = { id: 'listing', name: n, address: a, category: 'listing' as const, lat, lng, description: tl };
    const url = buildListingUrl(fakeAmenity);
    const embed = `<iframe\n  src="${url}"\n  width="100%"\n  height="540"\n  frameborder="0"\n  style="border-radius:12px;border:1px solid #e2d9cc;"\n  title="${n} Neighborhood Map"\n  allowfullscreen\n></iframe>`;
    setGeneratedUrl(url);
    setEmbedCode(embed);
  }, []);

  const runGeocode = useCallback(async () => {
    if (!name.trim() || !address.trim()) {
      setGeoError('Please fill in both the listing name and address.');
      return;
    }
    setGeocoding(true);
    setGeoError('');
    setSuggestions([]);
    setShowSuggestions(false);
    const coords = await geocodeAddress(address);
    setGeocoding(false);
    if (!coords) {
      setGeoError('Address not found. Try adding the city and state, e.g. "123 Main St Charlotte NC 28277"');
      return;
    }
    setPinned(coords);
    buildOutputs(name, address, coords.lat, coords.lng, tagline);
    const listing: SavedListing = {
      id: `${Date.now()}`,
      name, address,
      lat: coords.lat, lng: coords.lng,
      createdAt: new Date().toISOString(),
    };
    saveListing(listing);
    setSaved(loadSaved());
  }, [name, address, tagline, buildOutputs]);

  const pickSuggestion = (s: { label: string; lat: number; lng: number }) => {
    setAddress(s.label);
    setSuggestions([]);
    setShowSuggestions(false);
    setPinned({ lat: s.lat, lng: s.lng });
    buildOutputs(name || 'My Listing', s.label, s.lat, s.lng, tagline);
  };

  const onPinDrag = (lat: number, lng: number) => {
    setPinned({ lat, lng });
    buildOutputs(name, address, lat, lng, tagline);
  };

  const loadSavedListing = (sl: SavedListing) => {
    setName(sl.name);
    setAddress(sl.address);
    setPinned({ lat: sl.lat, lng: sl.lng });
    buildOutputs(sl.name, sl.address, sl.lat, sl.lng, '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeSaved = (id: string) => {
    deleteListing(id);
    setSaved(loadSaved());
  };

  // Distance from RE/MAX office
  const officeCoords = { lat: 35.0598, lng: -80.7893 }; // 12104 Copper Way Charlotte NC
  const distFromOffice = pinned
    ? (haversineDistanceMiles(officeCoords.lat, officeCoords.lng, pinned.lat, pinned.lng) * 1.35).toFixed(1)
    : null;

  return (
    <div className="min-h-screen" style={{ background: '#f7f4f0', fontFamily: "'Montserrat', sans-serif" }}>

      {/* Top nav */}
      <div className="sticky top-0 z-30 border-b shadow-md" style={{ background: BRAND.darkNavy, borderColor: BRAND.navy }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-semibold transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Map
            </button>
            <span className="text-white/30">|</span>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: BRAND.gold }} />
              <span className="text-white font-bold text-sm">Listing Map Creator</span>
            </div>
          </div>
          <img src="/images/remax-white.png" alt="RE/MAX Executive" className="h-7 object-contain" />
        </div>
        <div style={{ height: '2px', background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.red}, ${BRAND.gold})` }} />
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        {/* Hero */}
        <div className="text-center">
          <h1 className="text-3xl font-black mb-2" style={{ color: BRAND.darkNavy, fontFamily: 'Lora, serif' }}>
            Create a Neighborhood Map for Any Listing
          </h1>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">
            Type the address, hit <strong>Generate Map</strong>, and instantly get a shareable link + embed code. No coordinates. No code. No tech skills needed.
          </p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-xl border overflow-hidden" style={{ borderColor: '#e2d9cc' }}>

          {/* Card header */}
          <div className="px-6 py-4 border-b flex items-center gap-2" style={{ background: '#f8f5f0', borderColor: '#e2d9cc' }}>
            <MapPin className="w-4 h-4" style={{ color: BRAND.gold }} />
            <span className="font-bold text-sm uppercase tracking-widest" style={{ color: BRAND.darkNavy }}>Step 1 — Enter Property Details</span>
          </div>

          <div className="p-6 grid lg:grid-cols-2 gap-8">
            {/* Left: form */}
            <div className="space-y-4">

              {/* Listing name */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: BRAND.darkNavy }}>
                  Listing / Property Name <span style={{ color: BRAND.red }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. The Morgan at Ballantyne"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 transition-shadow"
                  style={{ borderColor: '#e2d9cc', '--tw-ring-color': BRAND.gold } as React.CSSProperties}
                />
              </div>

              {/* Address with suggestions */}
              <div className="relative">
                <label className="block text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: BRAND.darkNavy }}>
                  Property Address <span style={{ color: BRAND.red }}>*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="e.g. 5000 Ballantyne Commons Pkwy Charlotte NC 28277"
                    value={address}
                    onChange={(e) => { setAddress(e.target.value); setGeoError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && runGeocode()}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="w-full border rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 transition-shadow pr-10"
                    style={{ borderColor: '#e2d9cc', '--tw-ring-color': BRAND.gold } as React.CSSProperties}
                  />
                  {suggestionLoading && (
                    <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" style={{ color: BRAND.navy }} />
                  )}
                </div>

                {/* Autocomplete dropdown */}
                <AnimatePresence>
                  {showSuggestions && suggestions.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                      className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border rounded-xl shadow-xl overflow-hidden"
                      style={{ borderColor: '#e2d9cc' }}
                    >
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          onMouseDown={() => pickSuggestion(s)}
                          className="w-full text-left px-4 py-2.5 text-xs hover:bg-blue-50 border-b last:border-b-0 flex items-start gap-2 transition-colors"
                          style={{ borderColor: '#f0f0f0' }}
                        >
                          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: BRAND.navy }} />
                          <span className="text-gray-700 leading-snug">{s.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Optional tagline */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: '#888' }}>
                  Tagline / Description <span className="font-normal normal-case">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Stunning 4BR in top-rated school district"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  className="w-full border rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 transition-shadow"
                  style={{ borderColor: '#e2d9cc', '--tw-ring-color': BRAND.gold } as React.CSSProperties}
                />
              </div>

              {/* Error */}
              {geoError && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-start gap-2 p-3 rounded-xl border text-xs" style={{ background: '#fff5f5', borderColor: '#fecaca', color: BRAND.red }}>
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  {geoError}
                </motion.div>
              )}

              {/* Tip */}
              <div className="flex items-start gap-2 p-3 rounded-xl border text-xs" style={{ background: '#f8f5f0', borderColor: `${BRAND.gold}40`, color: '#666' }}>
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: BRAND.gold }} />
                <span>Start typing the address — suggestions will appear automatically. Select one or type the full address and click Generate.</span>
              </div>

              {/* Generate button */}
              <button
                onClick={runGeocode}
                disabled={geocoding || !name.trim() || !address.trim()}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-white font-black text-base tracking-wide transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                style={{ background: `linear-gradient(135deg, ${BRAND.darkNavy}, ${BRAND.navy})` }}
              >
                {geocoding ? (
                  <><Loader className="w-5 h-5 animate-spin" /> Finding on Map…</>
                ) : (
                  <><Sparkles className="w-5 h-5" style={{ color: BRAND.gold }} /> Generate Map</>
                )}
              </button>
            </div>

            {/* Right: map preview */}
            <div>
              <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: BRAND.darkNavy }}>
                Map Preview {pinned && <span className="font-normal normal-case text-gray-400 ml-1">— drag the pin to fine-tune</span>}
              </div>
              <div
                className="rounded-xl overflow-hidden border"
                style={{ height: '340px', borderColor: pinned ? BRAND.navy : '#e2d9cc', position: 'relative', background: '#e8e4dc' }}
              >
                {pinned ? (
                  <MiniMap lat={pinned.lat} lng={pinned.lng} name={name || 'Your Listing'} onDragEnd={onPinDrag} />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#f0ebe3' }}>
                      <MapPin className="w-8 h-8" style={{ color: '#ccc' }} />
                    </div>
                    <p className="text-sm font-semibold" style={{ color: '#aaa' }}>Your listing pin will appear here</p>
                    <p className="text-xs text-gray-400">Fill in the form and click Generate Map</p>
                  </div>
                )}
              </div>

              {/* Quick stats */}
              {pinned && distFromOffice && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-lg p-2.5 border text-xs" style={{ background: '#f8f5f0', borderColor: '#e2d9cc' }}>
                    <p className="text-gray-400 mb-0.5">From RE/MAX Office</p>
                    <p className="font-bold" style={{ color: BRAND.darkNavy }}>~{distFromOffice} miles</p>
                  </div>
                  <div className="rounded-lg p-2.5 border text-xs" style={{ background: '#f8f5f0', borderColor: '#e2d9cc' }}>
                    <p className="text-gray-400 mb-0.5">Coordinates</p>
                    <p className="font-mono font-bold text-xs" style={{ color: BRAND.darkNavy }}>{pinned.lat.toFixed(4)}, {pinned.lng.toFixed(4)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Output card */}
        <AnimatePresence>
          {generatedUrl && (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl border overflow-hidden"
              style={{ borderColor: '#e2d9cc' }}
            >
              <div className="px-6 py-4 border-b flex items-center gap-2" style={{ background: '#f8f5f0', borderColor: '#e2d9cc' }}>
                <Share2 className="w-4 h-4" style={{ color: BRAND.gold }} />
                <span className="font-bold text-sm uppercase tracking-widest" style={{ color: BRAND.darkNavy }}>Step 2 — Your Map is Ready!</span>
              </div>

              <div className="p-6 space-y-6">

                {/* Quick actions row */}
                <div className="flex flex-wrap gap-3">
                  <a
                    href={generatedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm font-bold px-5 py-3 rounded-xl text-white shadow-lg hover:opacity-90 transition-opacity"
                    style={{ background: `linear-gradient(135deg, ${BRAND.red}, #a00000)` }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Live Map
                  </a>
                  <CopyBtn text={generatedUrl} label="Copy Link" />
                  <a
                    href={`mailto:?subject=${encodeURIComponent(name + ' — Neighborhood Map')}&body=${encodeURIComponent('Check out the neighborhood map for this listing:\n\n' + generatedUrl)}`}
                    className="flex items-center gap-2 text-sm font-bold px-5 py-3 rounded-xl border transition-colors"
                    style={{ borderColor: BRAND.navy, color: BRAND.navy }}
                  >
                    📧 Email Link
                  </a>
                </div>

                {/* Shareable URL */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2" style={{ color: BRAND.darkNavy }}>
                    🔗 Shareable Link
                  </label>
                  <div className="flex gap-2">
                    <div
                      className="flex-1 font-mono text-xs border rounded-xl px-4 py-3 bg-gray-50 text-gray-600 overflow-x-auto whitespace-nowrap"
                      style={{ borderColor: '#e2d9cc' }}
                    >
                      {generatedUrl}
                    </div>
                    <CopyBtn text={generatedUrl} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Put this in your MLS listing, Zillow remarks, Instagram bio, or text it directly to buyers.
                  </p>
                </div>

                {/* Embed code */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2" style={{ color: BRAND.darkNavy }}>
                    <Code2 className="w-3.5 h-3.5 inline mr-1" />
                    Embed in Blog / Website
                  </label>
                  <div className="relative">
                    <pre
                      className="text-xs font-mono border rounded-xl p-4 bg-gray-50 text-gray-600 overflow-x-auto whitespace-pre leading-relaxed"
                      style={{ borderColor: '#e2d9cc', maxHeight: '110px' }}
                    >{embedCode}</pre>
                    <div className="absolute top-3 right-3">
                      <CopyBtn text={embedCode} label="Copy Code" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Paste into any WordPress post, Squarespace page, Wix block, or HTML email.
                  </p>
                </div>

                {/* Where to use guide */}
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    { icon: '🏡', title: 'MLS / Zillow', desc: 'Paste the link in your listing remarks or virtual tour URL field' },
                    { icon: '📱', title: 'Instagram / Social', desc: 'Shorten the URL with bit.ly, put it in your bio or story link' },
                    { icon: '🌐', title: 'Your Website', desc: 'Paste the embed code into any page block or post' },
                  ].map((item) => (
                    <div key={item.title} className="rounded-xl p-3 border" style={{ background: '#f8f5f0', borderColor: `${BRAND.gold}30` }}>
                      <div className="text-xl mb-1">{item.icon}</div>
                      <div className="font-bold text-xs mb-1" style={{ color: BRAND.darkNavy }}>{item.title}</div>
                      <div className="text-xs text-gray-500">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Recent listings */}
        {saved.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl border overflow-hidden" style={{ borderColor: '#e2d9cc' }}>
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background: '#f8f5f0', borderColor: '#e2d9cc' }}>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" style={{ color: BRAND.gold }} />
                <span className="font-bold text-sm uppercase tracking-widest" style={{ color: BRAND.darkNavy }}>Recent Listings</span>
              </div>
              <span className="text-xs text-gray-400">{saved.length} saved</span>
            </div>
            <div className="divide-y" style={{ borderColor: '#f0ebe3' }}>
              {saved.map((sl) => {
                const url = buildListingUrl({ id: 'listing', name: sl.name, address: sl.address, category: 'listing', lat: sl.lat, lng: sl.lng });
                return (
                  <div key={sl.id} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg"
                      style={{ background: BRAND.gold }}
                    >
                      🏠
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate" style={{ color: BRAND.darkNavy }}>{sl.name}</p>
                      <p className="text-xs text-gray-400 font-mono truncate">{sl.address}</p>
                      <p className="text-xs text-gray-300 mt-0.5">{new Date(sl.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => loadSavedListing(sl)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors"
                        style={{ borderColor: BRAND.navy, color: BRAND.navy }}
                      >
                        Edit
                        <ChevronRight className="w-3 h-3" />
                      </button>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                        style={{ background: BRAND.darkNavy }}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </a>
                      <button
                        onClick={() => removeSaved(sl.id)}
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-4 text-xs text-gray-400">
          <span style={{ color: BRAND.navy, fontWeight: 700 }}>SoldONDanielle.com</span>
          {' · '}Danielle Edwards · RE/MAX Executive · 704-604-2999
        </div>
      </div>
    </div>
  );
}
